import classNames from 'classnames';
import { Icon } from 'components';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import { generateColor, trimUninformativeString } from 'lib/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import './style.scss';

// A regex that detects punctuation marks
const PUNCT_MARK_RE = /[!$%^&*()_+|~=`{}\[\]:";'<>?,.\/]/;

const Avatar = ({
  url,
  name,
  className,
  color,
  isPrivate,
  onlineStatus,
  size = 'small',
  type,
  status,
  placeholder,
  outRoom,
  tooltipText,
  notScheduled,
  isAbsent,
  showAllergyTooltip = false,
  ...props
}) => {
  const [isValidUrl, setIsValidUrl] = useState(true);
  const flags = useLaunchDarkly();
  const dropInFlag = flags['online-cms-programs-dropins'];
  const allergy = flags['online-cms-allergy-icon'] && trimUninformativeString(props.allergy);

  useEffect(() => {
    if (url) {
      checkIfValidUrl(url).then(setIsValidUrl);
    }
  }, []);

  const getInitials = () => {
    if (props.initials) {
      return props.initials;
    }

    let initials = '';

    try {
      if (name) {
        initials = name
          .trim()
          .replace(PUNCT_MARK_RE, '')
          .split(/\s+/)
          .slice(0, 2)
          .map(name => name[0].toUpperCase())
          .join('');
      }
    } catch (e) {
      initials = 'NA';
    }

    return initials;
  };

  const getIconName = () => {
    switch (type) {
      case 'avatar':
        return 'person';

      case 'logo':
      case 'select-logo':
        return 'school';
    }
  };

  const checkIfValidUrl = url => {
    const img = new Image();
    img.src = url;
    return new Promise(resolve => {
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  const initials = getInitials();

  const avatarCN = classNames({
    avatar: true,
    [`avatar--${size}`]: true,
    'avatar--logo': type === 'logo',
    'avatar--select-logo': type === 'select-logo',
    [className]: Boolean(className),
    'avatar--offline': onlineStatus === 'offline',
    'avatar--drop-in-flag': dropInFlag
  });

  const pictureCN = classNames({
    avatar__picture: true,
    'avatar__picture--select-logo': type === 'select-logo'
  });

  const style = {
    backgroundColor:
      (url && isValidUrl) || type ? 'transparent' : color ? '#' + color : name ? generateColor(name) : 'transparent'
  };

  const initialsCN = classNames({
    avatar__initials: true,
    'avatar__initials--not-scheduled': notScheduled,
    'avatar__initials--absent': isAbsent,
    'avatar__initials--signed-in': onlineStatus === 'online' && dropInFlag
  });

  return (
    <div className={avatarCN}>
      <div className={initialsCN}>
        <div className="avatar__initials-bg" style={style}>
          {url && isValidUrl ? (
            <div className={pictureCN} style={{ backgroundImage: `url('${url}')` }} />
          ) : type ? (
            <div className="avatar__placeholder">
              <Icon resizeable name={getIconName()} />
              <span className="avatar__placeholder-tip">
                {placeholder || `Upload ${type === 'select-logo' ? 'logo' : type}`}
              </span>
            </div>
          ) : (
            <div className="avatar__initials-text">{initials}</div>
          )}
        </div>
      </div>

      {isPrivate && (
        <div className="avatar__private">
          <Icon name="private" size="16" />
        </div>
      )}

      {dropInFlag ? (
        <>
          {notScheduled ? (
            <div className="avatar__online-dropin-status avatar__online-dropin-status--not-scheduled">
              <Icon name="not-scheduled" size={30} color="#FFF" />
            </div>
          ) : onlineStatus === 'online' ? (
            <div className="avatar__online-dropin-status  avatar__online-dropin-status--circle">
              <Icon name="sign-in" size={22} />
            </div>
          ) : (
            isAbsent && (
              <div className="avatar__online-dropin-status">
                <Icon name="absent" size={30} />
              </div>
            )
          )}
        </>
      ) : (
        <>
          {onlineStatus && !notScheduled && <div className="avatar__online-status" />}
          {notScheduled && (
            <div className="avatar__online-not-scheduled">
              <Icon name="Unscheduled" size={16} color="#FFF" />
            </div>
          )}
        </>
      )}

      {status && (
        <div className="avatar__status">
          <Icon name={status} size="14" />
          <div className="avatar__status-title">{status}</div>
        </div>
      )}

      {outRoom && (
        <div className="avatar__out-room">
          <Icon name="out-room" size="12" />
        </div>
      )}

      {allergy && (
        <div className="avatar__allergy">
          <Icon name="allergy" size={null} />
          {showAllergyTooltip && <div className="avatar__allergy-title">Allergy</div>}
        </div>
      )}

      {!status && tooltipText && (
        <div className="avatar__tooltip">
          <div className="avatar__tooltip-title">{tooltipText}</div>
        </div>
      )}
    </div>
  );
};

Avatar.propTypes = {
  url: PropTypes.string,
  name: PropTypes.string,
  color: PropTypes.string,
  isPrivate: PropTypes.bool,
  initials: PropTypes.string,
  onlineStatus: PropTypes.oneOf(['online', 'offline']),
  size: PropTypes.oneOf(['xxs', 'xs', 'small', 'dashboard', 'medium', 'large']),
  type: PropTypes.oneOf(['avatar', 'logo', 'select-logo']),
  status: PropTypes.oneOf(['parent', 'admin', 'other']),
  placeholder: PropTypes.string,
  tooltipText: PropTypes.node,
  notScheduled: PropTypes.bool
};

export default Avatar;
